import { useEffect, useRef, useCallback, useState } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { wsEndpoint } from '../config/aws'
import { createWsConnection, type SocketLike, type WsHandlers } from './wsConnection'

export type { WsHandlers } from './wsConnection'
export { WS_RECONNECTED } from './wsConnection'

// One connection for the whole tab (see wsConnection.ts). Every call to useWebSocket
// subscribes to it instead of opening its own.
const connection = createWsConnection({
  getUrl: async () => {
    if (!wsEndpoint) {
      console.warn('WebSocket endpoint not configured')
      return null
    }
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (!token) {
      console.warn('No auth token for WebSocket connection')
      return null
    }
    return `${wsEndpoint}?token=${token}`
  },
  createSocket: (url) => new WebSocket(url) as unknown as SocketLike,
})

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    connection.handleVisibility(document.visibilityState === 'visible')
  })
  window.addEventListener('online', () => connection.handleOnline())
}

/**
 * Subscribe a view to live updates. `handlers` is keyed by the message action, and may
 * include `ws_reconnected`, which is sent after the socket comes back so the view can
 * refetch what it shows.
 */
export function useWebSocket(handlers?: WsHandlers) {
  const handlersRef = useRef<WsHandlers>(handlers || {})
  const [connected, setConnected] = useState(connection.isConnected())

  // Update handlers ref when handlers change
  useEffect(() => {
    handlersRef.current = handlers || {}
  }, [handlers])

  useEffect(() => {
    const unsubscribe = connection.subscribe(handlersRef)
    const stopListening = connection.onConnectionChange(setConnected)
    setConnected(connection.isConnected())
    return () => {
      stopListening()
      unsubscribe()
    }
  }, [])

  const send = useCallback((action: string, data?: Record<string, unknown>) => {
    connection.send(action, data)
  }, [])

  return { connected, send }
}
